import { appConfig } from '../config/appConfig';
import type { AuthResponse, AuthUser } from '../types/domain';
import { resolveApiErrorMessage } from '../utils/authFormUtils';
import { apiClient } from './apiClient';

type AuthMePayload = { success?: boolean; user?: AuthUser; error?: string };
type AuthCodeRequestPayload = {
  success?: boolean;
  data?: { verified?: boolean; expiresAt?: string; reused?: boolean; fallback?: boolean };
  message?: string;
  error?: string;
};

type CaptchaVerificationPayload = {
  captchaId: string;
  captchaOutput: string;
  lotNumber: string;
  passToken: string;
  genTime: string;
  phone: string;
  scene: 'signup' | 'signin' | 'password-change';
  validate?: string;
};

type SignUpCallbackPayload = {
  success?: boolean;
  data?: {
    matched?: boolean;
    success?: boolean;
    user?: AuthUser;
  };
  error?: string;
  message?: string;
};

type UserExistsPayload = {
  success?: boolean;
  data?: {
    exists?: boolean;
  };
  error?: string;
  message?: string;
};

function buildDevicePayload() {
  return { platform: 'web', deviceType: 'web', source: 'webapp2' };
}

export const WECHAT_WEBSITE_LOGIN_HINT =
  '微信网站扫码登录即将开通。请先使用小程序扫码，或用手机号登录。';

async function resolveAuthResult(payload: AuthResponse): Promise<AuthUser> {
  if (!payload.success || !payload.user || !payload.accessToken || !payload.expiresIn) {
    throw new Error(resolveApiErrorMessage(payload, '登录失败'));
  }
  await apiClient.storeAuthResult(payload);
  // Login payloads may omit capabilities (e.g. FactoryWorker); /auth/me is the source of truth.
  try {
    return await authService.fetchSession();
  } catch {
    return payload.user;
  }
}

export const authService = {
  async loginWithPassword(phone: string, password: string): Promise<AuthUser> {
    const payload = await apiClient.requestJson<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: { phone, password, device: buildDevicePayload(), refreshTransport: appConfig.refreshTransport },
      auth: false,
    });
    return resolveAuthResult(payload);
  },

  async loginWithAuthCode(phone: string, authCode: string): Promise<AuthUser> {
    const payload = await apiClient.requestJson<AuthResponse>('/api/auth/login-with-code', {
      method: 'POST',
      body: { phone, authCode, device: buildDevicePayload(), refreshTransport: appConfig.refreshTransport },
      auth: false,
    });
    return resolveAuthResult(payload);
  },

  async verifyCaptchaAndRequestAuthCode(payload: CaptchaVerificationPayload): Promise<{ message: string; expiresAt?: string; reused?: boolean; fallback?: boolean }> {
    const response = await apiClient.requestJson<AuthCodeRequestPayload>('/api/users/auth/verify', {
      method: 'POST',
      body: payload,
      auth: false,
    });

    if (!response.success || !response.data?.verified) {
      throw new Error(response.error || response.message || '验证失败');
    }

    return {
      message: response.message || '验证码已发送',
      expiresAt: response.data.expiresAt,
      reused: response.data.reused,
      fallback: response.data.fallback,
    };
  },

  async completeSignup(phone: string, authCode: string): Promise<void> {
    const payload = await apiClient.requestJson<SignUpCallbackPayload>('/api/users/auth/signup-callback', {
      method: 'POST',
      body: { phone, authCode },
      auth: false,
    });

    const matched = payload.data?.matched;
    const registrationSucceeded = payload.data?.success;
    if (!payload.success || matched === false || registrationSucceeded === false) {
      throw new Error(payload.error || payload.message || '注册失败');
    }

    if (matched !== true && registrationSucceeded !== true) {
      throw new Error(payload.error || payload.message || '注册状态异常，请稍后重试');
    }
  },

  async doesPhoneNumberExist(phone: string): Promise<boolean> {
    const payload = await apiClient.requestJson<UserExistsPayload>('/api/users/exists', {
      method: 'POST',
      body: { phone },
      auth: false,
    });

    if (!payload.success || typeof payload.data?.exists !== 'boolean') {
      throw new Error(payload.error || payload.message || '手机号校验失败');
    }

    return payload.data.exists;
  },

  async requestLoginAuthCode(phone: string): Promise<{ message: string; expiresAt?: string }> {
    const payload = await apiClient.requestJson<AuthCodeRequestPayload>('/api/users/auth/verify', {
      method: 'POST',
      body: { phone, scene: 'signin', validate: 'webapp2-local-fallback' },
      auth: false,
    });
    if (!payload.success || !payload.data?.verified) {
      throw new Error(payload.error || payload.message || '发送验证码失败');
    }
    return { message: payload.message || '验证码已发送', expiresAt: payload.data.expiresAt };
  },

  async verifyPasswordChangeCode(phone: string, authCode: string) {
    const payload = await apiClient.requestJson<{ success?: boolean; data?: { token?: string }; error?: string }>(
      '/api/auth/password-change/verify-code',
      { method: 'POST', body: { phone, authCode }, auth: false },
    );
    if (!payload.success || !payload.data?.token) {
      throw new Error(payload.error || '验证码校验失败');
    }
    return { token: payload.data.token };
  },

  async confirmPasswordChange(phone: string, authCode: string, token: string, newPassword: string) {
    const payload = await apiClient.requestJson<{ success?: boolean; data?: { updated?: boolean }; error?: string }>(
      '/api/auth/password-change/confirm',
      { method: 'POST', body: { phone, authCode, token, newPassword }, auth: false },
    );
    if (!payload.success || !payload.data?.updated) {
      throw new Error(payload.error || '密码重置失败');
    }
  },

  async fetchSession(): Promise<AuthUser> {
    const payload = await apiClient.requestJson<AuthMePayload>('/api/auth/me', { auth: true });
    if (!payload.user) throw new Error(payload.error || '未获取到登录用户');
    await apiClient.setCurrentUser(payload.user);
    return payload.user;
  },

  buildWechatLoginUrl(returnTo?: string): string {
    const origin = typeof window !== 'undefined' ? window.location.origin : appConfig.sitePublicUrl;
    const fallbackReturnTo = `${origin.replace(/\/+$/, '')}/login/oauth/wechat`;
    const targetReturnTo = (returnTo || fallbackReturnTo).trim();
    const params = new URLSearchParams({ returnTo: targetReturnTo });
    const onKelilearning = typeof window !== 'undefined'
      && /(?:^|\.)kelilearning\.com$/i.test(window.location.hostname);
    const startBase = onKelilearning
      ? origin.replace(/\/+$/, '')
      : appConfig.wechatOAuthStartUrl;
    return `${startBase.replace(/\/+$/, '')}/api/oauth/wechat/login?${params.toString()}`;
  },

  async requestWechatWebsiteAuthorizeUrl(returnTo?: string): Promise<string> {
    return this.buildWechatLoginUrl(returnTo);
  },

  async completeOAuthLogin(payload: {
    accessToken: string;
    refreshToken?: string;
    expiresIn?: number;
  }): Promise<AuthUser> {
    await apiClient.storeAuthResult({
      success: true,
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken,
      expiresIn: payload.expiresIn,
    });
    return authService.fetchSession();
  },

  async bindWechatWithPhone(payload: {
    bindToken: string;
    phone: string;
    authCode: string;
  }): Promise<AuthUser> {
    const response = await apiClient.requestJson<AuthResponse>('/api/oauth/wechat/bind', {
      method: 'POST',
      body: {
        bindToken: payload.bindToken,
        phone: payload.phone,
        authCode: payload.authCode,
        device: { platform: 'web', deviceType: 'web', source: 'webapp2-wechat-bind' },
        refreshTransport: appConfig.refreshTransport,
      },
      auth: false,
    });
    return resolveAuthResult(response);
  },

  async logout(): Promise<void> {
    try {
      await apiClient.requestJson('/api/auth/logout', { method: 'POST', auth: true });
    } catch {
      /* ignore */
    }
    await apiClient.clearSession();
  },

  async requestDeleteAccountSendCode(phone?: string): Promise<{
    message: string;
    phoneMasked?: string;
    expiresAt?: string;
    reused?: boolean;
  }> {
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: { phoneMasked?: string; expiresAt?: string; reused?: boolean };
      message?: string;
      error?: string;
    }>('/api/users/account/delete/send-code', {
      method: 'POST',
      body: phone ? { phone } : {},
      auth: true,
    });
    if (!payload.success) {
      throw new Error(payload.error || payload.message || '发送验证码失败');
    }
    return {
      message: payload.message || '验证码已发送',
      phoneMasked: payload.data?.phoneMasked,
      expiresAt: payload.data?.expiresAt,
      reused: payload.data?.reused,
    };
  },

  async deleteAccount(authCode: string, phone?: string): Promise<void> {
    const payload = await apiClient.requestJson<{ success?: boolean; error?: string }>(
      '/api/users/account/delete',
      {
        method: 'POST',
        body: {
          confirmation: 'DELETE_MY_ACCOUNT',
          authCode: authCode.trim(),
          ...(phone ? { phone } : {}),
        },
        auth: true,
      },
    );
    if (!payload.success) throw new Error(payload.error || '注销账号失败');
    await apiClient.clearSession();
  },
};
